import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { queue } from "@/components/Toast";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { useRefreshAiBudget } from "@/hooks/useAiBudget";
import type {
  ChatMessage,
  ChatModel,
  ChatSession,
  PersistedChatMessage,
  ReasoningEffort,
  StreamingToolEvent,
} from "@/types/chat.type";
import type { AgentFocus, NavigateResult, NavigateTarget } from "@/types/agentFocus.type";
import { resolveReasoningEffort } from "../components/agentChatUi";
import { useChatModelStore } from "@/stores/chatModelStore";
import {
  buildAssistantMessageMetadata,
  parseUsageFromSse,
} from "../components/chatMessageMetadata";
import {
  createStreamingTurnBuilder,
  type StreamingBlock,
} from "../components/streamingTurnBlocks";
import { splitSessionMessages, useResumableChatTurn } from "../components/resumeChatTurn";
import { newLiveSend, useStopChatTurn, type LiveSend } from "../components/stopChatTurn";
import { invalidateForFocus, invalidateForTool } from "./toolInvalidations";
import { useAgentRunStore } from "./agentRunStore";

import { withStrategySlideContext } from "../strategy/strategySlideContext";

class ChatStreamError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ChatStreamError";
  }
}

/**
 * Per-turn overrides. Arguments rather than state the caller sets beforehand
 * because sendMessage closes over the picker selection and the session id — a
 * caller that did setState-then-send would send the previous render's values.
 */
export type SendMessageOptions = {
  model?: ChatModel | null;
  reasoningEffort?: ReasoningEffort;
  /** Start a brand-new session for this turn, ignoring the currently open one. */
  newSession?: boolean;
  /** Send into this session instead of the open one. */
  sessionId?: string;
};

/**
 * The one chat, for whichever surface the user is on.
 *
 * The only structural difference from the three hooks it replaces: `focus` is
 * sent with every message and read from a ref, so a turn started on the
 * calendar stays a calendar turn even if the user walks to another page while
 * it streams, and the NEXT message picks up wherever they ended up.
 */
export function useBrandAgentChat(
  brandId: string,
  sessionId: string | null,
  focus: AgentFocus,
  callbacks?: {
    onSessionCreated?: (session: ChatSession) => void;
    onSessionsChanged?: () => void;
    /** The agent asked to move the user. The rail routes; this hook does not. */
    onNavigate?: (target: NavigateTarget) => void;
  },
) {
  const refreshAiBudget = useRefreshAiBudget();
  const queryClient = useQueryClient();
  const { onSessionCreated, onSessionsChanged, onNavigate } = callbacks ?? {};

  // Keep the live focus reachable from the memoized sendMessage without making
  // it a dependency (it changes identity on every navigation).
  const focusRef = useRef(focus);
  focusRef.current = focus;
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;

  const currentSessionKey = sessionId ? `${brandId}:${sessionId}` : null;
  const base = `/brand-agent/${brandId}/sessions`;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadedSessionKey, setLoadedSessionKey] = useState<string | null>(null);
  const [streamingBlocks, setStreamingBlocks] = useState<StreamingBlock[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  // Bumped when a live stream dies before its terminal event (a proxy closing an
  // idle connection on a long turn, a dropped network). Handing it to the resume
  // loop makes it follow the still-generating reply to completion in place.
  const [streamRecoveryKey, setStreamRecoveryKey] = useState(0);
  const [streamError, setStreamError] = useState<string | null>(null);
  const isStreamingRef = useRef(false);
  isStreamingRef.current = isStreaming;

  const [selectedModel, setSelectedModelState] = useState<ChatModel | null>(null);
  const persistedReasoningEffort = useChatModelStore((s) => s.reasoningEffort);
  const persistSelection = useChatModelStore((s) => s.setSelection);
  const persistReasoningEffort = useChatModelStore((s) => s.setReasoningEffort);
  const [selectedReasoningEffort, setSelectedReasoningEffortState] =
    useState<ReasoningEffort>(() => persistedReasoningEffort ?? "low");
  // Real context size (tokens) after the last reply, from the provider's usage.
  // null = no real usage yet, and the meter stays hidden until one lands.
  const [contextTokens, setContextTokens] = useState<number | null>(null);

  const setSelectedModel = useCallback(
    (model: ChatModel) => {
      const effort = resolveReasoningEffort(
        model.reasoningOptions,
        persistedReasoningEffort,
      );
      setSelectedModelState(model);
      setSelectedReasoningEffortState(effort);
      persistSelection(model.id, effort);
    },
    [persistedReasoningEffort, persistSelection],
  );

  const setSelectedReasoningEffort = useCallback(
    (effort: ReasoningEffort) => {
      setSelectedReasoningEffortState(effort);
      persistReasoningEffort(effort);
    },
    [persistReasoningEffort],
  );

  useEffect(() => {
    let cancelled = false;
    if (!sessionId || !currentSessionKey) {
      setMessages([]);
      setLoadedSessionKey(null);
      setContextTokens(null);
      return;
    }
    if (isStreaming) return;
    if (currentSessionKey === loadedSessionKey) return;
    setStreamError(null);

    void (async () => {
      try {
        const res = await axiosInstance.get<{ messages: PersistedChatMessage[] }>(
          `${base}/${sessionId}`,
        );
        if (cancelled) return;
        // A "streaming" tail row (a reply still generating server-side) is kept
        // out of history here; useResumableChatTurn renders it live and follows
        // it to completion.
        setMessages(splitSessionMessages(res.data.messages).messages);
        const lastUsage = [...res.data.messages]
          .reverse()
          .find((m) => m.metadata?.usage)?.metadata?.usage;
        setContextTokens(lastUsage?.contextTokens ?? null);
      } catch (error) {
        if (!cancelled) console.error("Failed to load agent chat session:", error);
      } finally {
        if (!cancelled) setLoadedSessionKey(currentSessionKey);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [base, brandId, currentSessionKey, sessionId, isStreaming, loadedSessionKey]);

  useResumableChatTurn({
    sessionKey: currentSessionKey,
    sessionUrl: sessionId ? `${base}/${sessionId}` : null,
    isStreamingRef,
    setters: { setIsStreaming, setStreamingBlocks, setMessages },
    onSettled: () => {
      refreshAiBudget();
      onSessionsChanged?.();
      // A resumed turn was not watched tool by tool, so refresh everything the
      // focused surface could have changed while we were not listening.
      invalidateForFocus(queryClient, {
        brandId,
        sessionId,
        focus: focusRef.current,
      });
    },
    recoveryKey: streamRecoveryKey,
  });

  const liveSendRef = useRef<LiveSend | null>(null);
  const { stopStreaming, isStopping } = useStopChatTurn({
    sessionId,
    abortUrlFor: (id) => `${base}/${id}/abort`,
    liveSendRef,
    isStreaming,
    onStalled: () => setStreamRecoveryKey((k) => k + 1),
  });

  const sendMessage = useCallback(
    async (content: string, opts?: SendMessageOptions) => {
      if (!content.trim() || isStreaming) return;
      const trimmed = withStrategySlideContext(content.trim(), focusRef.current, brandId);
      const model = opts?.model ?? selectedModel;
      const effort = opts?.reasoningEffort ?? selectedReasoningEffort;
      // Rebound mid-turn when the agent creates the strategy the turn writes to.
      let turnFocus = focusRef.current;

      setIsStreaming(true);
      useAgentRunStore.getState().start(turnFocus);
      setStreamingBlocks([]);
      setStreamError(null);

      let activeSessionId = opts?.newSession ? null : (opts?.sessionId ?? sessionId);
      const isNewSession = !activeSessionId;
      const turn = createStreamingTurnBuilder();
      let streamStarted = false;
      let userMessageId: string | null = null;
      const send = newLiveSend(activeSessionId);
      liveSendRef.current = send;

      try {
        if (!activeSessionId) {
          const res = await axiosInstance.post<ChatSession>(base);
          activeSessionId = res.data.id;
          onSessionCreated?.(res.data);
          send.sessionId = activeSessionId;
        }
        // Stopped before anything was sent: there is nothing to stop.
        if (send.stopped) return;

        const userMessage: ChatMessage = {
          id: `local-${Date.now()}`,
          workflowRunId: "",
          role: "user",
          content: trimmed,
          metadata: { focus: turnFocus },
          createdAt: new Date().toISOString(),
        };
        userMessageId = userMessage.id;
        setMessages((prev) => [...prev, userMessage]);
        // The server's row is created as the turn starts, so the reply sorts
        // right under its question — not below whatever the turn added meanwhile.
        const replyCreatedAt = new Date(Date.parse(userMessage.createdAt) + 1).toISOString();

        const response = await axiosInstance.post<ReadableStream<Uint8Array>>(
          `${base}/${activeSessionId}/message`,
          {
            message: trimmed,
            focus: turnFocus,
            ...(model?.id && model.id !== "auto" ? { model: model.id } : {}),
            ...(model?.reasoningOptions?.length ? { reasoningEffort: effort } : {}),
            turnId: send.id,
          },
          { responseType: "stream", adapter: "fetch" },
        );

        if (isNewSession) onSessionsChanged?.();

        const stream = response.data;
        if (!stream || typeof (stream as ReadableStream).getReader !== "function") {
          throw new Error("No response body");
        }

        streamStarted = true;
        const decoder = new TextDecoder();
        const reader = (stream as ReadableStream<Uint8Array>).getReader();
        let fullText = "";
        // Every thinking chunk, joined exactly as the server stores them, so the
        // offsets on each tool event slice the same text after a reload.
        let thinkingRaw = "";
        const toolEvents: StreamingToolEvent[] = [];
        let turnUsage = null as ReturnType<typeof parseUsageFromSse>;
        let navigateTo: NavigateTarget | null = null;
        // Set only by the turn's terminal frames. A stream that ends without
        // them was cut off, so the reply is still being generated server-side
        // and must be followed rather than finalized from what we received.
        let sawTerminal = false;
        // The server told us this turn was stopped by a user, so the partial
        // reply below is labelled rather than treated as a finished answer.
        let wasAborted = false;

        const processLine = (rawLine: string) => {
          const line = rawLine.trim();
          if (!line.startsWith("data:")) return;
          const jsonStr = line.slice(5).trim();
          if (jsonStr === "") return;
          if (jsonStr === "[DONE]") {
            sawTerminal = true;
            return;
          }

          try {
            const parsed = JSON.parse(jsonStr) as {
              type?: string;
              content?: string;
              fullText?: string;
              toolName?: string;
              toolCallId?: string;
              toolInput?: unknown;
              inputTokens?: number;
              outputTokens?: number;
              contextTokens?: number;
              model?: string;
              result?: unknown;
              error?: string;
            };

            if (parsed.error) throw new ChatStreamError(parsed.error);

            if (parsed.type === "thinking_chunk" && parsed.content) {
              thinkingRaw += parsed.content;
              setStreamingBlocks(turn.onThinkingChunk(parsed.content));
            } else if (parsed.type === "chunk" && parsed.content) {
              setStreamingBlocks(turn.onTextChunk(parsed.content));
              fullText += parsed.content;
            } else if (parsed.type === "aborted") {
              wasAborted = true;
            } else if (parsed.type === "complete" && parsed.fullText) {
              fullText = parsed.fullText;
              sawTerminal = true;
              setStreamingBlocks(turn.onStreamComplete(parsed.fullText));
            } else if (parsed.type === "usage") {
              const usage = parseUsageFromSse(parsed);
              if (usage) {
                turnUsage = usage;
                setContextTokens(usage.contextTokens ?? null);
              }
            } else if (parsed.type === "tool_start" && parsed.toolName) {
              const toolCallId =
                parsed.toolCallId ?? `${parsed.toolName}-${toolEvents.length}`;
              const event: StreamingToolEvent = {
                toolName: parsed.toolName,
                toolCallId,
                input: parsed.toolInput ?? null,
                result: null,
                status: "running",
                startedAt: new Date().toISOString(),
                endedAt: "",
                // Where the call sits in the turn — see splitTurnRounds.
                textOffset: fullText.length,
                thinkingOffset: thinkingRaw.length,
                thinkingMs: turn.thinkingMsSoFar(),
              };
              toolEvents.push(event);
              setStreamingBlocks(turn.onToolStart(event));
              useAgentRunStore.getState().toolStart(parsed.toolName, toolCallId, parsed.toolInput ?? null);
            } else if (parsed.type === "tool_end" && parsed.toolName) {
              const toolCallId =
                parsed.toolCallId ??
                toolEvents.find(
                  (e) => e.toolName === parsed.toolName && e.status === "running",
                )?.toolCallId;
              const event = toolCallId
                ? toolEvents.find((e) => e.toolCallId === toolCallId)
                : undefined;
              if (event) {
                event.result = parsed.result ?? null;
                event.status = "completed";
                event.endedAt = new Date().toISOString();
                setStreamingBlocks(
                  turn.onToolEnd(event.toolCallId, {
                    result: event.result,
                    status: event.status,
                    endedAt: event.endedAt,
                  }),
                );
              }
              useAgentRunStore.getState().toolEnd(parsed.toolName, toolCallId);
              // Every write tool saves immediately, so whatever it touched is
              // stale on screen the moment it returns.
              invalidateForTool(queryClient, parsed.toolName, {
                brandId,
                sessionId: activeSessionId,
                focus: turnFocus,
              });
              if (parsed.toolName === "CreateStrategy") {
                const result = parsed.result as { ok?: boolean; strategyId?: string } | null;
                // The agent made the strategy it is about to write: open it now,
                // so its slides fill in while the user watches, and bind the rest
                // of the turn (invalidations, the writing caption) to it. Only the
                // page's search params change, so nothing unmounts under the reply.
                if (result?.ok && result.strategyId && turnFocus.surface === "strategy") {
                  turnFocus = { surface: "strategy", strategyId: result.strategyId };
                  useAgentRunStore.setState({ focus: turnFocus });
                  onNavigateRef.current?.({ surface: "strategy", strategyId: result.strategyId });
                }
              }
              if (parsed.toolName === "Navigate") {
                const result = parsed.result as NavigateResult | null;
                // Held until the turn ends: the tool ends the turn server-side,
                // and routing mid-stream would unmount the page under a reply
                // that is still arriving.
                if (result?.ok && result.target) navigateTo = result.target;
              }
            }
          } catch (error) {
            if (error instanceof ChatStreamError) throw error;
            if (
              error instanceof Error &&
              error.message !== "Unexpected end of JSON input"
            ) {
              console.error("Error parsing SSE:", error);
            }
          }
        };

        // Buffer across reads: an SSE `data:` line is regularly split over two
        // network reads (big tool results, long final text). Parsing each read
        // in isolation silently drops the split line — including, at the worst
        // possible moment, the final `complete`.
        let buffer = "";
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            // Last element may be an incomplete line; hold it for the next read.
            buffer = lines.pop() ?? "";
            for (const line of lines) processLine(line);
          }
          // Flush a complete trailing line left after the stream ends.
          if (buffer.trim() !== "") processLine(buffer);
        } finally {
          reader.releaseLock();
        }

        if (!sawTerminal) {
          // Cut off mid-turn. The backend decouples generation from the socket,
          // so the reply is still being written to its row: hand off to the
          // resume loop, which follows that row to completion and reconciles.
          throw new ChatStreamError("Stream ended before the turn completed");
        }

        const { totalThinkingMs } = turn.finalize();

        const assistantMessage: ChatMessage = {
          id: `local-${Date.now() + 1}`,
          workflowRunId: "",
          role: "assistant",
          content: fullText,
          metadata: {
            ...buildAssistantMessageMetadata(
              thinkingRaw.trim() ? { text: thinkingRaw, durationMs: totalThinkingMs } : null,
              turnUsage,
            ),
            focus: turnFocus,
          },
          toolEvents:
            toolEvents.length > 0
              ? toolEvents.map((e) => ({
                  toolName: e.toolName,
                  toolCallId: e.toolCallId,
                  input: e.input,
                  result: e.result,
                  startedAt: e.startedAt,
                  endedAt: e.endedAt,
                  textOffset: e.textOffset,
                  thinkingOffset: e.thinkingOffset,
                  thinkingMs: e.thinkingMs,
                }))
              : undefined,
          createdAt: replyCreatedAt,
          ...(wasAborted ? { status: "aborted" as const } : {}),
        };

        setMessages((prev) => [...prev, assistantMessage]);
        setStreamingBlocks([]);
        onSessionsChanged?.();
        if (navigateTo) onNavigateRef.current?.(navigateTo);
      } catch (error) {
        console.error("M7 Agents chat error:", error);
        if (!streamStarted && isAxiosError(error) && error.response?.status === 409) {
          // This tab lost track of a reply the server is still writing. The new
          // message was not saved: take it back out, and follow that reply.
          setMessages((prev) => prev.filter((m) => m.id !== userMessageId));
          queue.add({
            title: "Your message wasn't sent",
            description: "A reply is still being written. Wait for it or stop it, then send again.",
            variant: "warning",
          });
          setStreamRecoveryKey((k) => k + 1);
        } else if (streamStarted) {
          // The connection carried at least the response head, so a turn row
          // exists and generation continues server-side. Hand off to resume.
          setStreamRecoveryKey((k) => k + 1);
        } else {
          setStreamError(
            getAiRequestErrorMessage(
              error,
              "The assistant stopped unexpectedly. Please try again.",
            ),
          );
        }
      } finally {
        if (liveSendRef.current === send) liveSendRef.current = null;
        setIsStreaming(false);
        useAgentRunStore.getState().end();
        setStreamingBlocks([]);
        refreshAiBudget();
      }
    },
    [
      base,
      brandId,
      queryClient,
      sessionId,
      isStreaming,
      selectedModel,
      selectedReasoningEffort,
      onSessionCreated,
      onSessionsChanged,
      refreshAiBudget,
    ],
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setLoadedSessionKey(null);
    setStreamingBlocks([]);
    setStreamError(null);
    setContextTokens(null);
  }, []);

  // A session is selected but its transcript hasn't been fetched yet.
  const isLoadingMessages =
    currentSessionKey !== null &&
    loadedSessionKey !== currentSessionKey &&
    !isStreaming &&
    messages.length === 0;

  return {
    messages,
    isLoadingMessages,
    streamingBlocks,
    isStreaming,
    streamError,
    sendMessage,
    stopStreaming,
    isStopping,
    clearMessages,
    selectedModel,
    setSelectedModel,
    selectedReasoningEffort,
    setSelectedReasoningEffort,
    contextTokens,
  };
}
